/**
 * Alex chat bubble — custom element <alex-chat> (snippets/alex-chat.liquid).
 *
 * The server decides who sees it (GET endpoint -> { mode, rollout_percent }):
 *   off      never shown
 *   hidden   only with ?alex=<preview token> (saved in localStorage; ?alex=off forgets it)
 *   percent  preview token, or bucket(visitor) < rollout_percent
 * Vanilla JS, no build. Visible strings come from data- attributes (locales chat.*).
 */
(() => {
  const CONFIG_TTL_MS = 60 * 1000; // an "off" switch reaches visitors within a minute
  const CONFIG_TIMEOUT_MS = 8 * 1000;
  const REQUEST_TIMEOUT_MS = 75 * 1000; // the server gives up on Claude after about 60 s
  const MAX_SAVED_MESSAGES = 60;
  const ACTIVE_CLASS = 'alex-chat-active';

  const store = {
    get(key, area = 'localStorage') {
      try { return window[area].getItem(key); } catch { return null; }
    },
    set(key, value, area = 'localStorage') {
      try { window[area].setItem(key, value); } catch { /* storage unavailable */ }
    },
    remove(key, area = 'localStorage') {
      try { window[area].removeItem(key); } catch { /* storage unavailable */ }
    },
  };

  // FNV-1a, identical to bucket() in the server (supabase/functions/chat/guardrails.ts).
  function bucket(visitorId) {
    let h = 2166136261;
    for (let i = 0; i < visitorId.length; i++) {
      h ^= visitorId.charCodeAt(i);
      h = Math.imul(h, 16777619) >>> 0;
    }
    return h % 100;
  }

  function shouldShow(cfg, visitorId, previewToken) {
    if (!cfg || typeof cfg.mode !== 'string') return false;
    if (cfg.mode === 'hidden') return previewToken !== '';
    if (cfg.mode === 'percent') {
      const percent = Number(cfg.rollout_percent);
      return previewToken !== '' || (Number.isFinite(percent) && bucket(visitorId) < percent);
    }
    return false;
  }

  function uuid() {
    return (window.crypto && crypto.randomUUID && crypto.randomUUID()) ||
      'v-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }

  // Text -> [{ text } | { url, label }]. Only http(s) links; trailing punctuation stays outside the link.
  function splitLinks(text) {
    const parts = [];
    let last = 0;
    for (const m of text.matchAll(/https?:\/\/[^\s<>"]+/g)) {
      const raw = m[0].replace(/[.,;:!?)\]]+$/, '');
      let url = null;
      try {
        const u = new URL(raw);
        if (u.protocol === 'http:' || u.protocol === 'https:') url = u.href;
      } catch { /* not a link */ }
      if (!url) continue;
      if (m.index > last) parts.push({ text: text.slice(last, m.index) });
      parts.push({ url, label: raw });
      last = m.index + raw.length;
    }
    if (last < text.length) parts.push({ text: text.slice(last) });
    return parts;
  }

  // **texte** devient du gras. Rien n'est injecté en HTML : tout passe par des noeuds de texte et createElement.
  function appendText(parent, text) {
    const pieces = text.split(/\*\*([^*\n][^*]*?)\*\*/);
    pieces.forEach((piece, i) => {
      if (i % 2 === 1) {
        const strong = document.createElement('strong');
        appendPlain(strong, piece);
        parent.append(strong);
      } else if (piece) {
        appendPlain(parent, piece);
      }
    });
  }

  function appendPlain(parent, text) {
    for (const part of splitLinks(text)) {
      if (part.url) {
        const a = document.createElement('a');
        a.href = part.url;
        a.textContent = part.label;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        parent.append(a);
      } else {
        parent.append(document.createTextNode(part.text));
      }
    }
  }

  function readJson(key, area) {
    try {
      return JSON.parse(store.get(key, area) || 'null');
    } catch {
      return null;
    }
  }

  class AlexChat extends HTMLElement {
    connectedCallback() {
      if (this._started) return;
      this._started = true;
      this.endpoint = this.dataset.endpoint;
      this.visitorId = store.get('alex_visitor') || uuid();
      store.set('alex_visitor', this.visitorId);
      const param = new URLSearchParams(location.search).get('alex');
      if (param === 'off') store.remove('alex_preview');
      else if (param) store.set('alex_preview', param);
      this.previewToken = store.get('alex_preview') || '';
      // Retour de la connexion au compte : le fragment porte un secret de session (ou une erreur). On l'efface de l'adresse.
      const back = location.hash.match(/alex_auth=([A-Za-z0-9_-]{20,128})/);
      const backError = /alex_auth_error=1/.test(location.hash);
      if (back || backError) history.replaceState(null, '', location.pathname + location.search);
      if (back) store.set('alex_auth', back[1], 'sessionStorage');
      this.justLoggedIn = !!back;
      this.loginFailed = backError;
      if (back || backError) store.set('alex_open', '1', 'sessionStorage');
      this.authSecret = store.get('alex_auth', 'sessionStorage') || '';
      // Si la cliente était connectée au SITE quand elle s'est connectée au chat, on lie les deux : dès qu'elle se déconnecte
      // du site (la page n'a plus de client), la session de chat se termine aussi. Sinon, durée courte côté serveur (20 minutes).
      if (back) store.set('alex_auth_linked', this.dataset.customer ? '1' : '0', 'sessionStorage');
      if (this.authSecret && store.get('alex_auth_linked', 'sessionStorage') === '1' && !this.dataset.customer) this.endAuth(false);
      this.conversationId = store.get('alex_conversation', 'sessionStorage') || '';
      this.busy = false;
      this.closed = false; // the server turned the assistant off or refused us: stop sending
      this.decide();
    }

    async loadConfig() {
      const cached = readJson('alex_config', 'sessionStorage');
      if (cached && Date.now() - cached.at < CONFIG_TTL_MS) return cached.cfg;
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), CONFIG_TIMEOUT_MS);
      try {
        const res = await fetch(this.endpoint, { method: 'GET', signal: ctrl.signal });
        if (!res.ok) return null;
        const cfg = await res.json();
        store.set('alex_config', JSON.stringify({ at: Date.now(), cfg }), 'sessionStorage');
        return cfg;
      } finally {
        clearTimeout(timer);
      }
    }

    async decide() {
      try {
        const cfg = await this.loadConfig();
        if (shouldShow(cfg, this.visitorId, this.previewToken)) this.render();
      } catch { /* the bubble stays hidden; the Gorgias chat is untouched */ }
    }

    render() {
      const d = this.dataset;
      document.documentElement.classList.add(ACTIVE_CLASS);
      this.hidden = false;
      this.replaceChildren();

      this.button = document.createElement('button');
      this.button.className = 'alex-chat__toggle';
      this.button.type = 'button';
      this.button.textContent = d.labelOpen;
      this.button.setAttribute('aria-expanded', 'false');
      this.button.setAttribute('aria-controls', 'alex-chat-panel');

      this.panel = document.createElement('section');
      this.panel.className = 'alex-chat__panel';
      this.panel.id = 'alex-chat-panel';
      this.panel.hidden = true;
      this.panel.setAttribute('role', 'dialog');
      this.panel.setAttribute('aria-labelledby', 'alex-chat-title');

      const head = document.createElement('header');
      head.className = 'alex-chat__head';
      const title = document.createElement('strong');
      title.id = 'alex-chat-title';
      title.className = 'alex-chat__title';
      title.textContent = d.title;
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'alex-chat__close';
      close.setAttribute('aria-label', d.labelClose);
      const x = document.createElement('span');
      x.setAttribute('aria-hidden', 'true');
      x.textContent = '×';
      close.append(x);
      head.append(title, close);

      this.log = document.createElement('div');
      this.log.className = 'alex-chat__log';
      this.log.setAttribute('role', 'log');
      this.log.setAttribute('aria-label', d.title);
      this.log.tabIndex = 0;

      const note = document.createElement('p');
      note.className = 'alex-chat__note';
      note.textContent = d.disclaimer;

      this.form = document.createElement('form');
      this.form.className = 'alex-chat__form';
      this.input = document.createElement('input');
      this.input.type = 'text';
      this.input.maxLength = 1000;
      this.input.required = true;
      this.input.autocomplete = 'off';
      this.input.enterKeyHint = 'send';
      this.input.placeholder = d.placeholder;
      this.input.setAttribute('aria-label', d.placeholder);
      this.sendButton = document.createElement('button');
      this.sendButton.type = 'submit';
      this.sendButton.className = 'alex-chat__send';
      this.sendButton.textContent = d.send;
      this.form.append(this.input, this.sendButton);

      this.authBar = document.createElement('div');
      this.authBar.className = 'alex-chat__auth';
      const authText = document.createElement('span');
      authText.textContent = d.connected;
      const authEnd = document.createElement('button');
      authEnd.type = 'button';
      authEnd.className = 'alex-chat__auth-end';
      authEnd.textContent = d.logout;
      authEnd.addEventListener('click', () => this.endAuth(true));
      this.authBar.append(authText, authEnd);
      this.updateAuthBar();

      this.panel.append(head, this.authBar, this.log, note, this.form);
      this.append(this.button, this.panel);

      this.button.addEventListener('click', () => this.toggle(true));
      close.addEventListener('click', () => this.toggle(false));
      this.panel.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.toggle(false);
      });
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.send();
      });

      const saved = readJson('alex_transcript', 'sessionStorage') || [];
      if (saved.length === 0) this.say('assistant', d.greeting, false);
      else saved.forEach((m) => this.say(m.role, m.text, false));

      if (this.loginFailed) {
        this.toggle(true, false);
        this.say('assistant', d.loginError, false);
      } else if (this.justLoggedIn) {
        // La cliente revient de la connexion : on le dit, puis on relance la conversation pour qu'Alex lise ses commandes.
        this.toggle(true, false);
        this.say('assistant', d.loggedIn, false);
        this.input.value = d.afterLogin;
        this.send();
      }

      // The contact page links to ?gs_open_chat (the Gorgias launcher, hidden while Alex is shown).
      if (new URLSearchParams(location.search).has('gs_open_chat')) this.toggle(true);
      else if (store.get('alex_open', 'sessionStorage') === '1') this.toggle(true, false);
    }

    toggle(open, focus = true) {
      this.panel.hidden = !open;
      this.button.hidden = open;
      this.button.setAttribute('aria-expanded', String(open));
      if (open) store.set('alex_open', '1', 'sessionStorage');
      else store.remove('alex_open', 'sessionStorage');
      if (open) this.log.scrollTop = this.log.scrollHeight;
      if (focus) (open ? this.input : this.button).focus();
    }

    say(role, text, save = true) {
      const p = document.createElement('p');
      p.className = 'alex-chat__msg alex-chat__msg--' + role;
      const who = document.createElement('span');
      who.className = 'alex-chat__who';
      who.textContent = (role === 'user' ? this.dataset.senderYou : this.dataset.senderAlex) + ' : ';
      p.append(who);
      appendText(p, text);
      this.log.append(p);
      this.log.scrollTop = this.log.scrollHeight;
      if (save) {
        const saved = readJson('alex_transcript', 'sessionStorage') || [];
        saved.push({ role, text });
        store.set('alex_transcript', JSON.stringify(saved.slice(-MAX_SAVED_MESSAGES)), 'sessionStorage');
      }
      return p;
    }

    updateAuthBar() {
      if (this.authBar) this.authBar.hidden = !this.authSecret;
    }

    // Fin de la connexion au compte : le serveur supprime la session (et son jeton), la bulle oublie le secret.
    endAuth(announce) {
      const secret = this.authSecret;
      this.authSecret = '';
      store.remove('alex_auth', 'sessionStorage');
      store.remove('alex_auth_linked', 'sessionStorage');
      if (secret) {
        try {
          fetch(this.endpoint.replace(/\/$/, '') + '/auth/logout', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ auth_secret: secret }),
            keepalive: true,
          }).catch(() => {});
        } catch { /* hors ligne : la session expire d'elle-même */ }
      }
      this.updateAuthBar();
      // La conversation contenait des données du compte : on l'efface de l'écran et de la mémoire de l'onglet
      // (le serveur, lui, repart d'une conversation neuve), pour que la personne suivante sur cet appareil ne voie rien.
      this.conversationId = '';
      store.remove('alex_conversation', 'sessionStorage');
      store.remove('alex_transcript', 'sessionStorage');
      if (this.log) {
        this.log.replaceChildren();
        this.say('assistant', this.dataset.greeting, false);
        if (announce) this.say('assistant', this.dataset.loggedOut, false);
      }
    }

    // Bouton « Se connecter » : page de connexion de Shopify, puis retour à la même page (la conversation est gardée dans l'onglet).
    addLoginButton() {
      const here = location.origin + location.pathname + location.search;
      const u = new URL(this.endpoint, location.href);
      u.pathname = u.pathname.replace(/\/$/, '') + '/auth/start';
      u.searchParams.set('visitor_id', this.visitorId);
      u.searchParams.set('return_to', here);
      if (this.previewToken) u.searchParams.set('pt', this.previewToken);
      const a = document.createElement('a');
      a.className = 'alex-chat__login';
      a.href = u.href;
      a.textContent = this.dataset.login;
      a.addEventListener('click', () => store.set('alex_open', '1', 'sessionStorage'));
      this.log.append(a);
      this.log.scrollTop = this.log.scrollHeight;
    }

    // The server refused us or switched the assistant off: say so, stop sending, give the Gorgias chat back.
    shutDown() {
      this.closed = true;
      store.remove('alex_config', 'sessionStorage');
      document.documentElement.classList.remove(ACTIVE_CLASS);
    }

    async send() {
      if (this.busy || this.closed) return;
      const text = this.input.value.trim();
      if (!text) return;
      this.busy = true;
      this.sendButton.disabled = true;
      this.log.setAttribute('aria-busy', 'true');
      this.input.value = '';
      this.say('user', text);
      const typing = this.say('assistant', this.dataset.typing, false);
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), REQUEST_TIMEOUT_MS);
      let reply = '';
      let needsLogin = false;
      try {
        const res = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            visitor_id: this.visitorId,
            message: text,
            conversation_id: this.conversationId || undefined,
            preview_token: this.previewToken || undefined,
            auth_secret: this.authSecret || undefined,
            lang: this.dataset.lang,
          }),
          signal: ctrl.signal,
        });
        const data = await res.json().catch(() => ({}));
        if (data.conversation_id) {
          this.conversationId = data.conversation_id;
          store.set('alex_conversation', this.conversationId, 'sessionStorage');
        }
        if (data.auth_expired) {
          this.authSecret = '';
          store.remove('alex_auth', 'sessionStorage');
          store.remove('alex_auth_linked', 'sessionStorage');
          this.updateAuthBar();
        }
        needsLogin = data.needs_login === true;
        if (res.status === 403) store.remove('alex_preview');
        if (res.status === 403 || (res.status === 503 && data.error === 'disabled')) this.shutDown();
        reply = typeof data.reply === 'string' ? data.reply.trim() : '';
      } catch { /* network, timeout or invalid answer: generic message below */ }
      typing.remove();
      this.say('assistant', reply || this.dataset.error, reply !== '');
      if (needsLogin) this.addLoginButton();
      clearTimeout(timer);
      this.busy = false;
      this.sendButton.disabled = this.closed;
      this.input.disabled = this.closed;
      this.log.removeAttribute('aria-busy');
      if (!this.panel.hidden && !this.closed) this.input.focus();
    }
  }

  AlexChat.bucket = bucket;
  AlexChat.shouldShow = shouldShow;
  AlexChat.splitLinks = splitLinks;
  AlexChat.appendText = appendText;
  if (!customElements.get('alex-chat')) customElements.define('alex-chat', AlexChat);
})();
